import React, {createContext, useContext, useEffect} from 'react';
import {LoadingScreen} from '../../../../AppInterface/elements/Loaders/LoadingScreen.js';
import {pubsub} from '../../../../Core/PubSub.js';
import {useQuery} from '@tanstack/react-query';
import {ContextError} from '../../../../AppInterface/elements/Errors/ContextError.js';
import fonts from '../../../Fonts/font-config.json';
import {getPollById, restartPoll, savePollAccessPolicy, savePollPrivacyPolicy, savePollSchedule, startPoll, stopPoll} from '../../../../AppInterface/lib/api/PollsAPI.ts';
import {getPollStatus} from './getPollStatus';
import {pollDefaults} from './pollDefaults';


/**
 * @type {React.Context<API.Poll<{include: 'permissions' | 'languages' | 'layout'}> & {
 *     currentStatus: 'offline' | 'online' | 'starting' | 'ending' | 'draft',
 *     isDraft: boolean,
 *     hasNextStart: boolean,
 *     hasNextStop: boolean,
 *     isFinished: boolean,
 *     isRunning: boolean,
 * }>}
 */
export const PollContext = createContext();

export const PollProvider = ({pollId, ...props}) => {

    const {
        data: axiosResponse,
        isLoading,
        isFetching,
        dataUpdatedAt,
        isError,
        refetch
    } = useQuery({
            queryKey: ['poll', pollId],
            queryFn: () =>
                getPollById(pollId, {include: ['permissions', 'languages', 'layout', 'fonts']}),
            placeholderData: (prev) => prev
        }
    );

    // Support legacy Forms
    useEffect(() => {
        const subs = [];
        subs.push(pubsub.subscribe('languagesChanged', () => refetch()));

        return () => subs.forEach(sub => sub.remove());
    }, [refetch]);

    if (isLoading) {
        return <LoadingScreen/>;
    }
    if (isError || !axiosResponse) {
        return <ContextError refresh={false}/>;
    }

    let poll = axiosResponse.data;
    let actions = {};

    // This context is used in AppInterface and SurveyInterface but the following code is only needed for the AppInterface
    // Default values are set because backend does not deliver empty values
    // For SurveyInterface, no permissions are returned from backend
    if (poll.permissions) {
        poll = {
            ...poll,
            startedAt: poll.startedAt ?? pollDefaults.startedAt,
            closedAt: poll.closedAt ?? pollDefaults.closedAt,
            name: poll.name ?? pollDefaults.name,
            title: poll.title ?? pollDefaults.title,
            description: poll.description ?? pollDefaults.description,
            notice: poll.notice ?? pollDefaults.notice,
            settings: {
                nextStartAt: poll.settings?.nextStartAt ?? null,
                nextStopAt: poll.settings?.nextStopAt ?? null,
                access: {
                    ...pollDefaults.settings.access,
                    ...poll.settings?.access,
                },
                privacy: {
                    ...poll.settings?.privacy,
                }
            },
            state: {
                ...poll.state,
                currentStatus: getPollStatus(poll),
                isDraft: !poll.startedAt && !poll.settings?.nextStartAt,
                hasNextStart: !!poll.settings?.nextStartAt,
                hasNextStop: !!poll.settings?.nextStopAt,
                isFinished: !!poll.closedAt,
            },
            permissions: {
                ...poll.permissions,
                changeAnonymStatus: (poll.state?.responsesCount == 0 && poll.permissions.publish)
            }
        };


        actions = {
            savePrivacyPolicy(privacyPolicy) {
                poll.settings.privacy = privacyPolicy;
                return savePollPrivacyPolicy(poll.id, privacyPolicy).finally(refetch);
            },
            saveAccessPolicy(accessPolicy) {
                return savePollAccessPolicy(poll.id, accessPolicy).finally(refetch);
            },
            saveSchedule(start, stop) {
                return savePollSchedule(poll.id, {'nextStartAt': start, 'nextStopAt': stop}).finally(refetch);
            },
            start() {
                return startPoll(poll.id).finally(refetch);
            },
            stop() {
                return stopPoll(poll.id).finally(refetch);
            },
            restart() {
                return restartPoll(poll.id).finally(refetch); //currently not used
            }

        };
    }

    return <PollContext.Provider
        value={{poll, refetch, dataUpdatedAt, actions, isFetching}} {...props} />;
};

/**
 *
 * @return {typeof PollContext}
 */
export const usePoll = () => {
    const context = useContext(PollContext);
    if (context === undefined) {
        throw new Error(`usePoll must be used within a PollProvider`);
    }
    return context;
};

let areFontsImported = false;
export const usePollFonts = () => {
    const context = useContext(PollContext);
    if (context === undefined) {
        throw new Error(`usePollFonts must be used within a PollProvider`);
    }

    const publicFonts = [...fonts.additionalFonts, ...fonts.commonFonts],
        accountFonts = context.poll.fonts || [],
        allFonts = {accFonts: accountFonts, publicFonts: publicFonts};

    if (!areFontsImported) {
        areFontsImported = true;
        fonts.additionalFonts.map(font => import(`../../../Fonts/AdditionalFonts/${font}.css`));
        accountFonts.map(font => import(`../../../Fonts/AccountFonts/${font}.css`));
        window.fonts = allFonts; // support legacy responding
    }

    return allFonts;
};

